import { css } from '@emotion/react'
import { memo, useCallback, useMemo } from 'react'
import { VList } from 'virtua'
import type { Participant } from '../../../../../types'
import type { RoomType } from '../../../../../types/mfc'
import { setFullWindow } from '../../../../actions/layout'
import { openMenuDialog } from '../../../../actions/menu'
import { openMessage } from '../../../../actions/message'
import { openQuickMenu } from '../../../../app-state/modals/state.confirmation'
import { closeDrawer } from '../../../../app-state/modals/state.drawer'
import { ListType } from '../../../../constants/enums'
import {
    isLevelAdmin,
    isLevelBasic,
    isLevelModel,
    isLevelPremium,
} from '../../../../mfcLogic/userProperties/level'
import { isLounge } from '../../../../mfcLogic/userProperties/user'
import { getNameStyles } from '../../../../utilities/colors'
import i18n from '../../../../utilities/i18n'
import { safeObj } from '../../../../utilities/safe'
import { TransitionTypes } from '../../../../utilities/transitions'
import IconButton from '../../../Buttons/IconButton'
import { useList } from '../../../Hooks/useList'
import { useModelColor } from '../../../Hooks/useModelColor'
import { useRoomSelector } from '../../../Hooks/useRoom'
import { useScreenStateSelector } from '../../../Hooks/useScreenState'
import { useUserSelector } from '../../../Hooks/useUser'
import Message from '../../../Icons/Message'
import BadgeAvatar from '../../../common/BadgeAvatar'
import { getThemeSpacing } from '../../../styles/appTheme'
import { palette } from '../../../styles/colors'

const AVATAR_SIZE = 40
const AVATAR_REDUCED_SIZE = 24
const AVATAR_SIZE_SIDEBAR = 26
const VERTICAL_PADDING = getThemeSpacing(0.5, true)
const VERTICAL_PADDING_SIDEBAR = 1.5
const ROW_HEIGHT = AVATAR_SIZE + 2 * VERTICAL_PADDING
const ROW_HEIGHT_SIDEBAR = AVATAR_SIZE_SIDEBAR + 2 * VERTICAL_PADDING_SIDEBAR

const rootStyles = css({
    width: '100%',
    height: '100%',
})
const subtitleStyles = css({
    fontSize: 11,
    color: palette.secondaryText,
    fontWeight: 700,
})
const participantContainerStyles = css({
    width: `calc(100% - ${2 * getThemeSpacing(2, true)}px) !important`,
    marginLeft: getThemeSpacing(2),
    marginRight: getThemeSpacing(2),
})
const participantContainerSidebarStyles = css({
    marginLeft: 0,
    marginRight: 0,
    borderBottom: 'none',
    '&:last-of-type': {
        borderBottom: 'none',
    },
})
const participantRowStyles = css({
    cursor: 'pointer',
})
const participantStyles = css({
    display: 'flex',
    position: 'relative' as const,
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: VERTICAL_PADDING,
    paddingBottom: VERTICAL_PADDING,
})
const participantSidebarStyles = css({
    display: 'flex',
    position: 'relative' as const,
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: VERTICAL_PADDING_SIDEBAR,
    paddingBottom: VERTICAL_PADDING_SIDEBAR,
})
const nameStyles = css({
    marginLeft: getThemeSpacing(2),
    fontSize: '1rem',
})
const nameSidebarStyles = css({
    marginLeft: getThemeSpacing(0.75),
    fontSize: '0.8125rem',
})
const nameReducedSizeStyles = css({
    marginLeft: getThemeSpacing(0.75),
    fontSize: 12,
})
const buttonStyles = css({
    position: 'absolute' as const,
    right: getThemeSpacing(1),
})
const guestsStyles = css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center' as const,
    fontSize: '1rem',
    fontWeight: 700,
})
const guestsSidebarStyles = css({
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: getThemeSpacing(4),
})
const sectionLabelStyles = css({
    fontSize: 11,
    fontWeight: 400,
    color: palette.secondaryText,
    letterSpacing: '0.5px',
    paddingTop: getThemeSpacing(1),
    paddingBottom: getThemeSpacing(0.5),
})
const sectionLabelPadding = css({
    paddingLeft: getThemeSpacing(2),
})

interface GuestViewerCount {
    guestCount?: number
    viewerCount?: number
}

interface ParticipantExtensions {
    recentTipper?: boolean
    isFriend?: boolean
    isHighestCumulativeTipper?: boolean
    isHighestSingleTipper?: boolean
    sectionLabel?: string
}

type ParticipantType = Partial<Participant> &
    GuestViewerCount &
    ParticipantExtensions

interface ParticipantProps {
    isSidebar: boolean
    participant: ParticipantType
    streamType: RoomType
    highestSingleTipIcon?: string
    highestCumulativeTipIcon?: string
}

const ParticipantRow = memo((props: ParticipantProps) => {
    const {
        participant,
        isSidebar,
        streamType,
        highestSingleTipIcon,
        highestCumulativeTipIcon,
    } = props
    const {
        id,
        guestCount,
        viewerCount,
        sectionLabel,
        isHighestSingleTipper,
        isHighestCumulativeTipper,
    } = participant
    const slug = useUserSelector(id, 'slug')
    const name = useUserSelector(id, 'name')
    const level = useUserSelector(id, 'level')
    const color = useUserSelector(id, 'color')
    const isOffline = useUserSelector(id, 'isOffline')
    const isModel = useUserSelector(id, 'isModel')

    const reduceFontSize =
        isHighestSingleTipper &&
        isHighestCumulativeTipper &&
        participant.name.length >= 13

    const avatarSize = isSidebar
        ? reduceFontSize
            ? AVATAR_REDUCED_SIZE
            : AVATAR_SIZE_SIDEBAR
        : AVATAR_SIZE
    const userChatColor = useModelColor(color)
    const nameStyle = getNameStyles(level, userChatColor)

    const onClick = useCallback(
        (e) => {
            if (isModel) {
                closeDrawer()
                openMenuDialog(slug, { room: true })
            } else {
                openQuickMenu(
                    id,
                    {
                        x: e.clientX,
                        y: e.clientY,
                    },
                    true,
                )
            }
        },
        [slug, isModel],
    )

    const onMessageClick = useCallback(() => {
        closeDrawer()
        setFullWindow(false)
        openMessage(
            slug,
            isSidebar ? TransitionTypes.SIDEBAR : TransitionTypes.MODAL,
        )
    }, [slug, isSidebar])

    if (sectionLabel) {
        return (
            <div css={[sectionLabelStyles, !isSidebar && sectionLabelPadding]}>
                {sectionLabel}
            </div>
        )
    }

    if (viewerCount !== undefined) {
        return (
            <div css={subtitleStyles}>
                {i18n.t('chat.participants')} ({viewerCount})
            </div>
        )
    }

    if (guestCount !== undefined) {
        const paidShow =
            streamType === 'club' ||
            streamType === 'group' ||
            streamType === 'private'

        if (paidShow) {
            return
        }

        const guestCountText = isSidebar
            ? `(${guestCount} Guests)`
            : `Guests: ${guestCount}`
        return (
            <div css={isSidebar ? guestsSidebarStyles : guestsStyles}>
                {guestCountText}
            </div>
        )
    }

    return (
        <div
            css={
                !isSidebar
                    ? participantContainerStyles
                    : participantContainerSidebarStyles
            }
        >
            <div
                css={!isSidebar ? participantStyles : participantSidebarStyles}
            >
                <div css={participantRowStyles} onClick={onClick}>
                    <BadgeAvatar
                        disableBadge={true}
                        offline={isOffline}
                        small={isSidebar}
                        size={avatarSize}
                        alt={name}
                        id={id}
                    />
                    <span
                        css={
                            isSidebar
                                ? reduceFontSize
                                    ? nameReducedSizeStyles
                                    : nameSidebarStyles
                                : nameStyles
                        }
                        style={nameStyle}
                        data-testid="Container__ParticipantName"
                    >
                        {name}
                        {isHighestCumulativeTipper && (
                            <span title="Highest Total Tips!">
                                {' '}
                                {highestCumulativeTipIcon
                                    ? decodeURIComponent(
                                          highestCumulativeTipIcon,
                                      )
                                    : '⭐'}
                            </span>
                        )}
                        {isHighestSingleTipper && (
                            <span title="Highest Single Tip!">
                                {' '}
                                {highestSingleTipIcon
                                    ? decodeURIComponent(highestSingleTipIcon)
                                    : '🌟'}
                            </span>
                        )}
                    </span>
                </div>
                {!isSidebar && (
                    <IconButton css={buttonStyles} onClick={onMessageClick}>
                        <Message />
                    </IconButton>
                )}
            </div>
        </div>
    )
})

const getSectionLabel = (priority: number) => {
    switch (priority) {
        case 1:
            return 'Room Model'
        case 2:
            return 'Visiting Models'
        case 3:
            return 'Admins'
        case 4:
            return 'Friends'
        case 5:
            return 'Tippers'
        case 6:
            return 'Premium Members'
        case 7:
            return 'Basic Members'
        default:
            return 'Other'
    }
}

const itemKeySelector = (item: ParticipantType) => {
    const { id, guestCount, viewerCount, sectionLabel } = safeObj(item)
    if (id) return id
    if (guestCount !== undefined) return 'guestCount'
    if (viewerCount !== undefined) return 'viewerCount'
    if (sectionLabel) return `section-${sectionLabel}`
    return 'unknown'
}

interface IParticipants {
    id: number
    availableHeight?: number
    sidebar?: boolean
}

const Participants = memo((props: IParticipants) => {
    const vertical = useScreenStateSelector('portrait')
    const { id, availableHeight, sidebar = false } = props
    const leftNav = !vertical
    const streamType = useUserSelector(id, 'streamType')
    const roomParticipants = useRoomSelector(id, 'participants') ?? []
    const recentTippers = useUserSelector(id, 'recentTippers') ?? []
    const guestCount = useRoomSelector(id, 'guestCount') ?? 0
    const viewerCount = roomParticipants.length + guestCount
    const contentHeight = useScreenStateSelector('contentHeight')
    const resolvedHeight = sidebar ? contentHeight : availableHeight
    const friendsListIds = useList(ListType.FRIENDS, { returnIDs: true })
    const highestSingleTipper = useUserSelector(id, 'highestSingleTipper')
    const highestCumulativeTipper = useUserSelector(
        id,
        'highestCumulativeTipper',
    )
    const highestSingleTipperIcon = useUserSelector(
        id,
        'highestSingleTipperEmoji',
    )
    const highestCumulativeTipperIcon = useUserSelector(
        id,
        'highestCumulativeTipperEmoji',
    )
    const hideGuestCount = isLounge(id)

    const getSortPriority = useCallback(
        (user) => {
            const isRoomModel = user.id === id
            const isVisitingModel = isLevelModel(user.level) && user.id !== id
            const isAdmin = isLevelAdmin(user.level)
            const isFriend = friendsListIds.includes(user.id)
            const isTipper = recentTippers.includes(user.id)
            const isPremium = isLevelPremium(user.level)
            const isBasic = isLevelBasic(user.level)

            if (isRoomModel) return 1
            if (isVisitingModel) return 2
            if (isAdmin) return 3
            if (isFriend && !isTipper) return 4
            if (isTipper) return 5
            if (isPremium) return 6
            if (isBasic) return 7
            return 8 // should never happen
        },
        [id, friendsListIds, recentTippers],
    )

    const list = useMemo(() => {
        const listWithAttributes = Array.from(roomParticipants).map((u) => {
            return {
                ...u,
                ...(recentTippers.includes(u.id) ? { recentTipper: true } : {}),
                ...(friendsListIds.includes(u.id) ? { isFriend: true } : {}),
                ...(u.id === highestSingleTipper
                    ? { isHighestSingleTipper: true }
                    : {}),
                ...(u.id === highestCumulativeTipper
                    ? { isHighestCumulativeTipper: true }
                    : {}),
            }
        })

        return listWithAttributes.sort((first, second) => {
            const firstPriority = getSortPriority(first)
            const secondPriority = getSortPriority(second)

            // First sort by priority category
            if (firstPriority !== secondPriority) {
                return firstPriority - secondPriority
            }

            // Special sorting for recent tippers
            if (firstPriority === 5) {
                const getSpecialTipperOrder = (user) => {
                    // Highest cumulative tipper goes first
                    if (user.id === highestCumulativeTipper) return 0
                    // Highest single tipper goes second (unless they're the same as cumulative)
                    if (
                        user.id === highestSingleTipper &&
                        user.id !== highestCumulativeTipper
                    )
                        return 1
                    // Everyone else ordered by recent tips (starting from position 2)
                    return recentTippers.indexOf(user.id) + 2
                }

                return (
                    getSpecialTipperOrder(first) - getSpecialTipperOrder(second)
                )
            }

            // For all other sections, sort alphabetically
            return first.name?.toLowerCase() > second.name?.toLowerCase()
                ? 1
                : -1
        })
    }, [
        id,
        recentTippers,
        roomParticipants,
        friendsListIds,
        highestSingleTipper,
        highestCumulativeTipper,
        getSortPriority,
    ])

    const participants = useMemo<Array<ParticipantType>>(() => {
        const insertSectionLabels = (sortedList: Array<ParticipantType>) => {
            const result: Array<ParticipantType> = []
            // eslint-disable-next-line fp/no-let
            let lastPriority: number | null = null

            sortedList.forEach((user) => {
                const currentPriority = getSortPriority(user)
                // Add section label for each priority
                if (currentPriority !== lastPriority) {
                    result.push({
                        sectionLabel: getSectionLabel(currentPriority),
                    } as ParticipantType)
                }
                result.push(user)
                lastPriority = currentPriority
            })

            return result
        }

        const listWithLabels = insertSectionLabels(list)

        return sidebar
            ? [
                  { viewerCount },
                  ...listWithLabels,
                  ...(!hideGuestCount ? [{ guestCount }] : []),
              ]
            : [...listWithLabels, ...(!hideGuestCount ? [{ guestCount }] : [])]
    }, [
        list,
        sidebar,
        guestCount,
        hideGuestCount,
        viewerCount,
        getSortPriority,
    ])

    const rowHeight = sidebar ? ROW_HEIGHT_SIDEBAR : ROW_HEIGHT
    const listLength = list.length + (sidebar ? 2 : 1)
    const listHeight = listLength * rowHeight
    const rootStylesDrawer = css({
        width: '100%',
        height: !leftNav
            ? `min(${listHeight}px, calc(max(250px, 70vh) - 48px))`
            : '100%',
    })
    const styles = sidebar ? rootStyles : rootStylesDrawer

    const rows = useMemo(
        () =>
            participants.map((item) => (
                <ParticipantRow
                    key={itemKeySelector(item)}
                    participant={item}
                    isSidebar={sidebar}
                    streamType={streamType}
                    highestSingleTipIcon={highestSingleTipperIcon}
                    highestCumulativeTipIcon={highestCumulativeTipperIcon}
                />
            )),
        [
            participants,
            sidebar,
            streamType,
            highestSingleTipperIcon,
            highestCumulativeTipperIcon,
        ],
    )

    return (
        <div css={styles} data-testid="Modal__Participants">
            {resolvedHeight && (
                <VList
                    className="no-scrollbars"
                    style={{ height: resolvedHeight, width: '100%' }}
                    itemSize={rowHeight}
                >
                    {rows}
                </VList>
            )}
        </div>
    )
})

export default Participants
